import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { LAST_UPDATED_ASSORTMENT_LOG } from '@repo/constants'
import { EVENT_TYPES, getUserFullname } from '@repo/utils'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

export const auditTrailsSearchSchema = z.object({
  createdAt: z.string().optional(),
  eventType: z.array(z.enum(EVENT_TYPES)).optional(),
  keyword: z.string().optional(),
  ...datatableSearchSchema.shape,
})

export type AuditTrailsSearch = z.infer<typeof auditTrailsSearchSchema>

async function getAuditTrails(queries: AuditTrailsSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].$get(
    { query: stringifyValues(queries) },
    { init: { signal } },
  )

  const { auditTrails, count } = await res.json()
  return { auditTrails, count }
}

async function getAuditTrailById(id: number | undefined, signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'][':id'].$get(
    { param: { id: String(id) } },
    { init: { signal } },
  )

  const { auditTrail } = await res.json()
  return auditTrail
}

async function getLastUpdatedAssortmentLog(signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].assortment.last.$get({}, { init: { signal } })

  const updatedAssortmentLog = await res.json()
  return updatedAssortmentLog
}

async function getUser(userId: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.users[':userId'].$get(
    {
      param: { userId },
    },
    { init: { signal } },
  )

  const user = await res.json()
  return user
}

async function getLastUpdatedPricingAssumptionLog(
  pricingAssumptionId: string,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api['audit-trails'].crt['pricing-assumptions'][':id'].$get(
    {
      param: { id: pricingAssumptionId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedTaxAssumptionLog(taxAssumptionId: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['tax-assumptions'][':id'].$get(
    {
      param: { id: taxAssumptionId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedChannelMixLog(channelMixId: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['channel-mix'][':id'].$get(
    {
      param: { id: channelMixId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedUnreleasedMaterialsLog(
  unreleasedMaterialsId: string,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api['audit-trails'].crt['unreleased-materials'][':id'].$get(
    {
      param: { id: unreleasedMaterialsId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedProductTypeVariantLog(
  productTypeVariantId: string,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api['audit-trails'].crt['product-type-variants'][':id'].$get(
    {
      param: { id: productTypeVariantId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedAgreementLog(agreementId: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt.agreements[':id'].$get(
    {
      param: { id: agreementId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedSalesDealActualsLog(salesDealActualId: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['sales-deal-actuals'][':id'].$get(
    {
      param: { id: salesDealActualId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedFederalExciseLog(signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['federal-excise'].last.$get(
    {
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedCostOfGoodsLog(costOfGoodsId: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['cost-of-goods'][':id'].$get(
    {
      param: { id: costOfGoodsId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedBpcActualsLog(bpcActualsId: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['bpc-actuals'][':id'].$get(
    {
      param: { id: bpcActualsId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedRapForecastsLog(rapForecastId: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['rap-forecasts'][':id'].$get(
    {
      param: { id: rapForecastId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedRetailerRatesLog(signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['retailer-rates'].last.$get(
    {
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedProductTypeMappingsLog(signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['product-type-mappings'].last.$get(
    {
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedAgreementMappingsLog(signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['agreement-mappings'].last.$get(
    {
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getLastUpdatedConsumerRatesLog(signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['consumer-rates'].last.$get(
    {
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

async function getConfigurationMenuLogs(signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].assortment['configuration-menu'].$get(
    {},
    { init: { signal } },
  )

  const lastSync = await res.json()
  return lastSync
}

export type AuditTrails = Awaited<ReturnType<typeof getAuditTrails>>

export function useAuditTrails(queries: AuditTrailsSearch) {
  return useQuery({
    queryKey: ['audit-trails', queries],
    queryFn: ({ signal }) => getAuditTrails(queries, signal),
  })
}

export type AuditTrail = Awaited<ReturnType<typeof getAuditTrailById>>

export function useAuditTrailById(id: number | undefined) {
  return useQuery({
    queryKey: ['audit-trail', id],
    queryFn: ({ signal }) => getAuditTrailById(id, signal),
    enabled: Boolean(id),
  })
}

export function useLastUpdatedAssortmentLog() {
  return useQuery({
    queryKey: [LAST_UPDATED_ASSORTMENT_LOG],
    queryFn: ({ signal }) => getLastUpdatedAssortmentLog(signal),
  })
}

export function useLastUpdatedPricingAssumptionLog(pricingAssumptionId: string) {
  return useQuery({
    queryKey: ['lastUpdatedPricingAssumptionLog', pricingAssumptionId],
    queryFn: ({ signal }) => getLastUpdatedPricingAssumptionLog(pricingAssumptionId, signal),
  })
}

export function useLastUpdatedTaxAssumptionLog(taxAssumptionId: string) {
  return useQuery({
    queryKey: ['lastUpdatedTaxAssumptionLog', taxAssumptionId],
    queryFn: ({ signal }) => getLastUpdatedTaxAssumptionLog(taxAssumptionId, signal),
  })
}

export function useLastUpdatedChannelMixLog(channelMixId: string) {
  return useQuery({
    queryKey: ['lastUpdatedChannelMixLog', channelMixId],
    queryFn: ({ signal }) => getLastUpdatedChannelMixLog(channelMixId, signal),
  })
}

export function useLastUpdatedUnreleasedMaterialsLog(unreleasedMaterialsId: string) {
  return useQuery({
    queryKey: ['lastUpdatedUnreleasedMaterialsLog', unreleasedMaterialsId],
    queryFn: ({ signal }) => getLastUpdatedUnreleasedMaterialsLog(unreleasedMaterialsId, signal),
  })
}

export function useLastUpdatedProductTypeVariantLog(productTypeVariantId: string) {
  return useQuery({
    queryKey: ['lastUpdatedProductTypeVariantLog', productTypeVariantId],
    queryFn: ({ signal }) => getLastUpdatedProductTypeVariantLog(productTypeVariantId, signal),
  })
}

export function useLastUpdatedAgreementLog(agreementId: string) {
  return useQuery({
    queryKey: ['lastUpdatedAgreementLog', agreementId],
    queryFn: ({ signal }) => getLastUpdatedAgreementLog(agreementId, signal),
  })
}

export function useLastUpdatedSalesDealActualsLog(salesDealActualId: string) {
  return useQuery({
    queryKey: ['lastUpdatedSalesDealActualsLog', salesDealActualId],
    queryFn: ({ signal }) => getLastUpdatedSalesDealActualsLog(salesDealActualId, signal),
  })
}

export function useLastUpdatedFederalExciseLog() {
  return useQuery({
    queryKey: ['lastUpdatedFederalExciseLog'],
    queryFn: ({ signal }) => getLastUpdatedFederalExciseLog(signal),
  })
}

export function useLastUpdatedCostOfGoodsLog(costOfGoodsId: string) {
  return useQuery({
    queryKey: ['lastUpdatedCostOfGoodsLog', costOfGoodsId],
    queryFn: ({ signal }) => getLastUpdatedCostOfGoodsLog(costOfGoodsId, signal),
  })
}

export function useLastUpdatedRapForecastsLog(rapForecastId: string) {
  return useQuery({
    queryKey: ['lastUpdatedRapForecastsLog', rapForecastId],
    queryFn: ({ signal }) => getLastUpdatedRapForecastsLog(rapForecastId, signal),
  })
}

export function useLastUpdatedBpcActualsLog(bpcActualsId: string) {
  return useQuery({
    queryKey: ['lastUpdatedBpcActualsLog', bpcActualsId],
    queryFn: ({ signal }) => getLastUpdatedBpcActualsLog(bpcActualsId, signal),
  })
}

async function getLastUpdatedIpAccrualsLog(ipAccrualId: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['ip-accruals'][':id'].$get(
    {
      param: { id: ipAccrualId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

export function useLastUpdatedIpAccrualsLog(ipAccrualId: string) {
  return useQuery({
    queryKey: ['lastUpdatedIpAccrualsLog', ipAccrualId],
    queryFn: ({ signal }) => getLastUpdatedIpAccrualsLog(ipAccrualId, signal),
  })
}

async function getLastUpdatedCalculationRunAccrualsLog(
  calculationRunAccrualId: string,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api['audit-trails'].crt['calculation-run-accruals'][':id'].$get(
    {
      param: { id: calculationRunAccrualId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

export function useLastUpdatedCalculationRunAccrualsLog(calculationRunAccrualId: string) {
  return useQuery({
    queryKey: ['lastUpdatedCalculationRunAccrualsLog', calculationRunAccrualId],
    queryFn: ({ signal }) =>
      getLastUpdatedCalculationRunAccrualsLog(calculationRunAccrualId, signal),
  })
}

async function getLastUpdatedBudgetAllocationsLog(
  budgetAllocationId: string,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api['audit-trails'].crt['budget-allocations'][':id'].$get(
    {
      param: { id: budgetAllocationId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

export function useLastUpdatedBudgetAllocationsLog(budgetAllocationId: string) {
  return useQuery({
    queryKey: ['lastUpdatedBudgetAllocationsLog', budgetAllocationId],
    queryFn: ({ signal }) => getLastUpdatedBudgetAllocationsLog(budgetAllocationId, signal),
  })
}

export function useConfigurationMenuLogs() {
  return useQuery({
    queryKey: ['configurationMenuLogs'],
    queryFn: ({ signal }) => getConfigurationMenuLogs(signal),
  })
}

export function useLastUpdatedRetailerRatesLog() {
  return useQuery({
    queryKey: ['lastUpdatedRetailerRatesLog'],
    queryFn: ({ signal }) => getLastUpdatedRetailerRatesLog(signal),
  })
}

export function useLastUpdatedProductTypeMappingsLog() {
  return useQuery({
    queryKey: ['lastUpdatedProductTypeMappingsLog'],
    queryFn: ({ signal }) => getLastUpdatedProductTypeMappingsLog(signal),
  })
}

export function useLastUpdatedAgreementMappingsLog() {
  return useQuery({
    queryKey: ['lastUpdatedAgreementMappingsLog'],
    queryFn: ({ signal }) => getLastUpdatedAgreementMappingsLog(signal),
  })
}

async function getLastUpdatedRetailerMarginsFmcLog(signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['retailer-margins'].fmc.last.$get(
    {
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

export function useLastUpdatedRetailerMarginsFmcLog() {
  return useQuery({
    queryKey: ['lastUpdatedRetailerMarginsFmcLog'],
    queryFn: ({ signal }) => getLastUpdatedRetailerMarginsFmcLog(signal),
  })
}

async function getLastUpdatedRetailerMarginsNcLog(signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['retailer-margins'].nc.last.$get(
    {
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

export function useLastUpdatedRetailerMarginsNcLog() {
  return useQuery({
    queryKey: ['lastUpdatedRetailerMarginsNcLog'],
    queryFn: ({ signal }) => getLastUpdatedRetailerMarginsNcLog(signal),
  })
}

async function getLastUpdatedWholesalerMarginsFmcLog(signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['wholesaler-margins'].fmc.last.$get(
    {
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

export function useLastUpdatedWholesalerMarginsFmcLog() {
  return useQuery({
    queryKey: ['lastUpdatedWholesalerMarginsFmcLog'],
    queryFn: ({ signal }) => getLastUpdatedWholesalerMarginsFmcLog(signal),
  })
}

async function getLastUpdatedWholesalerMarginsNcLog(signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['wholesaler-margins'].nc.last.$get(
    {
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

export function useLastUpdatedWholesalerMarginsNcLog() {
  return useQuery({
    queryKey: ['lastUpdatedWholesalerMarginsNcLog'],
    queryFn: ({ signal }) => getLastUpdatedWholesalerMarginsNcLog(signal),
  })
}

export function useLastUpdatedConsumerRatesLog() {
  return useQuery({
    queryKey: ['lastUpdatedConsumerRatesLog'],
    queryFn: ({ signal }) => getLastUpdatedConsumerRatesLog(signal),
  })
}

async function getLastUpdatedSubChannelAllocationsLog(signal?: AbortSignal) {
  const api = getApi()
  const res = await api['audit-trails'].crt['sub-channel-allocations'].last.$get(
    {
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

export function useLastUpdatedSubChannelAllocationsLog() {
  return useQuery({
    queryKey: ['lastUpdatedSubChannelAllocationsLog'],
    queryFn: ({ signal }) => getLastUpdatedSubChannelAllocationsLog(signal),
  })
}

async function getLastUpdatedNcProvincialContributionsLog(
  ncProvincialContributionId: string,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api['audit-trails'].crt['nc-provincial-contributions'][':id'].$get(
    {
      param: { id: ncProvincialContributionId },
      query: { orderDirection: 'desc', limit: '1' },
    },
    { init: { signal } },
  )

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy, signal)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

export function useLastUpdatedNcProvincialContributionsLog(ncProvincialContributionId: string) {
  return useQuery({
    queryKey: ['lastUpdatedNcProvincialContributionsLog', ncProvincialContributionId],
    queryFn: ({ signal }) =>
      getLastUpdatedNcProvincialContributionsLog(ncProvincialContributionId, signal),
  })
}

async function getLastUpdatedChannelContributionsLog(channelContributionId: string) {
  const api = getApi()
  const res = await api['audit-trails'].crt['channel-contributions'][':id'].$get({
    param: { id: channelContributionId },
    query: { orderDirection: 'desc', limit: '1' },
  })

  const { auditTrails } = await res.json()

  if (!auditTrails?.length) return

  const user = await getUser(auditTrails[0].createdBy)

  return {
    createdBy: auditTrails[0].createdBy,
    createdAt: auditTrails[0].createdAt,
    userFullName: user ? getUserFullname(user) : undefined,
  }
}

export function useLastUpdatedChannelContributionsLog(channelContributionId: string) {
  return useQuery({
    queryKey: ['lastUpdatedChannelContributionsLog', channelContributionId],
    queryFn: () => getLastUpdatedChannelContributionsLog(channelContributionId),
  })
}
